import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Archive, ArchiveRestore, Trash2, Pencil, Brain } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function timeAgo(d?: string | null) {
  if (!d) return "—";
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  return `${days}d`;
}

export default function ResearchChatLayout() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { sessionId } = useParams();
  const [showArchived, setShowArchived] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameText, setRenameText] = useState("");

  const { data: sessions } = useQuery({
    queryKey: ["research_chat_sessions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_chat_sessions")
        .select("id, title, last_message_at, message_count, is_archived, updated_at")
        .order("last_message_at", { ascending: false, nullsFirst: false })
        .limit(200);
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    const ch = supabase
      .channel("research_chat_sessions_list")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "research_chat_sessions" },
        () => qc.invalidateQueries({ queryKey: ["research_chat_sessions"] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [qc]);

  const visible = useMemo(
    () => (sessions ?? []).filter((s) => (showArchived ? true : !s.is_archived)),
    [sessions, showArchived],
  );

  async function newChat() {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      toast.error("Not signed in");
      return;
    }
    const { data, error } = await supabase
      .from("research_chat_sessions")
      .insert({ user_id: u.user.id })
      .select("id")
      .single();
    if (error) {
      toast.error(error.message);
      return;
    }
    qc.invalidateQueries({ queryKey: ["research_chat_sessions"] });
    nav(`/partner/research/chat/${data.id}`);
  }

  async function rename(id: string) {
    if (!renameText.trim()) {
      setRenamingId(null);
      return;
    }
    await supabase.from("research_chat_sessions").update({ title: renameText.trim() }).eq("id", id);
    setRenamingId(null);
    qc.invalidateQueries({ queryKey: ["research_chat_sessions"] });
  }

  async function archive(id: string, val: boolean) {
    await supabase.from("research_chat_sessions").update({ is_archived: val }).eq("id", id);
    qc.invalidateQueries({ queryKey: ["research_chat_sessions"] });
  }

  async function del(id: string) {
    if (!confirm("Delete this conversation? This cannot be undone.")) return;
    await supabase.from("research_chat_sessions").delete().eq("id", id);
    qc.invalidateQueries({ queryKey: ["research_chat_sessions"] });
    if (sessionId === id) nav("/partner/research/chat");
  }

  return (
    <div className="flex h-[calc(100vh-14rem)] min-h-[500px] border rounded-lg overflow-hidden bg-background">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r flex flex-col bg-muted/30">
        <div className="p-3 border-b space-y-2">
          <Button onClick={newChat} className="w-full" size="sm">
            <Plus className="h-4 w-4 mr-1" /> New chat
          </Button>
          <NavLink
            to="/partner/research/chat/memory"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-muted",
                isActive && "bg-muted text-primary font-medium",
              )
            }
          >
            <Brain className="h-3.5 w-3.5" /> Memory
          </NavLink>
        </div>

        <div className="px-3 py-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>Conversations</span>
          <button onClick={() => setShowArchived((v) => !v)} className="hover:text-foreground">
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
          {visible.length === 0 && (
            <div className="text-xs text-muted-foreground p-3 text-center">No conversations yet.</div>
          )}
          {visible.map((s) => {
            const active = sessionId === s.id;
            return (
              <div
                key={s.id}
                className={cn(
                  "group rounded px-2 py-1.5 text-sm cursor-pointer flex items-start gap-1",
                  active ? "bg-primary/10 text-primary" : "hover:bg-muted",
                )}
                onClick={() => nav(`/partner/research/chat/${s.id}`)}
              >
                <div className="flex-1 min-w-0">
                  {renamingId === s.id ? (
                    <Input
                      autoFocus
                      value={renameText}
                      onChange={(e) => setRenameText(e.target.value)}
                      onBlur={() => rename(s.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") rename(s.id);
                        if (e.key === "Escape") setRenamingId(null);
                      }}
                      className="h-6 text-xs"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div className="font-medium truncate">{s.title}</div>
                  )}
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <span>{timeAgo(s.last_message_at ?? s.updated_at)}</span>
                    {s.message_count > 0 && (
                      <Badge variant="outline" className="h-4 px-1 text-[10px]">{s.message_count}</Badge>
                    )}
                    {s.is_archived && <span>· archived</span>}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <button className="opacity-0 group-hover:opacity-100 p-1 hover:bg-background rounded">
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem
                      onClick={() => {
                        setRenameText(s.title);
                        setRenamingId(s.id);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => archive(s.id, !s.is_archived)}>
                      {s.is_archived ? (
                        <><ArchiveRestore className="h-3.5 w-3.5 mr-2" /> Unarchive</>
                      ) : (
                        <><Archive className="h-3.5 w-3.5 mr-2" /> Archive</>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => del(s.id)} className="text-destructive">
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      </aside>

      {/* Main pane */}
      <div className="flex-1 min-w-0">
        <Outlet context={{ sessions, newChat }} />
      </div>
    </div>
  );
}
